import React, { isValidElement, useCallback, useState, useMemo } from "react";
import { MarketTemplateProps } from "../types";
import { MarketLayout } from "../components/MarketLayout/MarketLayout";
import { Card } from "components/src/components/Card/Card";
import { EventHeader } from "./components/EventHeader";
import { Nav } from "components/src/components/Nav/Nav";
import { type PillNavItemObjType } from "components/src/components/Nav/types";
import cx from "components/src/utils/cx";

import isEmpty from "lodash-es/isEmpty";

const _defaultOnSelectionClick = () => {};

const sportsWithDividers = {
  tennis: "vs",
  americanfootball: "at",
  football: "at",
  baseball: "at",
  basketball: "at",
  hockey: "at",
};

export const Squares = ({
  id,
  header,
  marketHeader = {},
  showMarketHeader = true,
  markets = [],
  marketFooter,
  footer = {},
  showBorder = false,
  showVersusDivider = false,
  type = "squares",
  versusText = "vs",
  groupBy = "group",
  showLimit = "all",
  filters,
  sport,
  league,
  competitionType,
  canShowLess = true,
  oddsFormat = "american",
  initiallyOpen = true,
  eventDetails,
  showMoreWagers = false,
  hasSGP = false,
  hasLivestream,
  isOddsBoost,
  hasFirebets = false,
  mode = "light",
  animation = true,
  corner,
  enableInternalSkeleton,
  showEventHeader,
  showTeamsInEventHeader = false,
  showEventHeaderChevron = true,
  swapEventHeaderCompetitors,
  eventHeaderCompetitionType,
  topPadNoHeader = "cui-pt-sm",
  className = "",
  onSelectionClick = _defaultOnSelectionClick,
  onHeaderClick,
  onEventHeaderClick,
  onTemplateClick,
  onFilterClick,
  updateMarket = () => {},
  updateMarkets = () => {},
  isSelectedBuilder,
  priceDisplayFormatter,
  ...rest
}: MarketTemplateProps) => {
  const [templateClicked, setTemplateClicked] = useState(false);
  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const [hasBeenToggled, setHasBeenToggled] = useState(false);
  const [_activeFilter, setActiveFilter] = useState<string | number | null>(
    "all"
  );

  const hasHeader = !isEmpty(header);
  const hasFilters = filters && Array.isArray(filters) && filters.length;

  const onActiveItem = useCallback(
    (item) => {
      if (filters && filters.length) {
        const [active] = (filters as PillNavItemObjType[]).filter((filter) => {
          return (
            typeof filter === "object" && "id" in filter && filter?.id === item
          );
        });
        setActiveFilter(active?.label);
        onFilterClick && onFilterClick(active);
      } else {
        setActiveFilter("all");
      }
    },
    [_activeFilter]
  );

  const _onTemplateClick = useCallback(
    (e) => {
      if (
        typeof onTemplateClick === "function" &&
        e.target?.offsetParent?.nodeName !== "BUTTON" &&
        e.target?.nodeName !== "BUTTON"
        // Restore if they don't want the Competitors to trigger a template click
        // (e.target?.offsetParent?.id.includes("cui__card") || e.target?.offsetParent?.id.includes("cui__market"))
      ) {
        onTemplateClick(e);
      }
    },
    [onTemplateClick]
  );

  const _onToggle = useCallback((payload) => {
    const { isOpen, id } = payload;
    setIsOpen(isOpen);
    if (!hasBeenToggled) setHasBeenToggled(true);
    onHeaderClick && onHeaderClick(payload);
  }, []);

  const handleNavClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Temporarily disabled
  // const _showVersusDivider =
  //   (sport && sport in sportsWithDividers) || showVersusDivider;
  // const _verusText =
  //   _showVersusDivider && sport ? sportsWithDividers[sport] : versusText;

  // const _header = { ...header, initiallyOpen };
  // const _collapsible =
  //   typeof header?.collapsible === "boolean" ? header?.collapsible : true;

  const cardClasses = useMemo(()=>{
    return cx({
        [className]: !!className,
        [topPadNoHeader as string]: typeof topPadNoHeader === "string" && (typeof header !== "object" || (!!header && !header?.hasHeader))
      },"cui-cursor-pointer")
  },[className, topPadNoHeader, header]);

  const hasEventDetails =
    typeof eventDetails !== "undefined" &&
    !isEmpty(eventDetails) &&
    Boolean(
      eventDetails?.eventName ||
        eventDetails?.isLive ||
        eventDetails?.startTime ||
        eventDetails?.home ||
        eventDetails?.away ||
        eventDetails?.competitor1 ||
        eventDetails?.competitor2
    );

  const _showEventHeader = hasEventDetails && showEventHeader;

  const _hasMarketHeader = showMarketHeader && (marketHeader?.title || marketHeader?.description);

  // @ts-expect-error
  const isValidSquaresSelections = markets && Array.isArray(markets?.at(0).selections) && Number.isInteger(
    // @ts-expect-error
    Math.sqrt(markets?.at(0)?.selections?.length)
  );

  return (
    <Card
      preventHeaderClickPropagation={true}
      {...rest}
      id={`${id}`}
      {...header?.computedHeader}
      collapsible={header?.collapsible}
      className={cardClasses}
      showBorder={showBorder}
      initiallyOpen={initiallyOpen}
      onToggle={_onToggle}
      onCardClick={_onTemplateClick}
      animation={animation}
      role="group"
    >
      {header && header?.isReactNode ? (
        <Card.Header>{header?.component}</Card.Header>
      ) : null}

      <Card.SubHeader>
        {_showEventHeader ? (
          <EventHeader
            id={id}
            isOddsBoost={isOddsBoost}
            onEventHeaderClick={onEventHeaderClick}
            eventDetails={eventDetails}
            sport={sport}
            showTeamsInEventHeader={showTeamsInEventHeader}
            mode={mode}
            showChevron={showEventHeaderChevron}
            swapCompetitors={swapEventHeaderCompetitors}
            competitionType={eventHeaderCompetitionType}
          />
        ) : null}
        {hasFilters ? (
          <Nav
            className="cui-mb-xs"
            size="xs"
            items={filters}
            cssMode={true}
            showControls="none"
            overlay={true}
            onActiveItem={onActiveItem}
            skipLink={null}
            tabbable={false}
            animation={animation}
            variant="pill"
            simple
            forceMaxWidth
            onClick={handleNavClick}
          />
        ) : null}
        {_hasMarketHeader ? (
          <MarketLayout.Header
            {...{
              title: marketHeader.title,
              description: marketHeader.description,
              noBorder: true,
            }}
            className="cui-mb-2"
          />
        ) : null}
      </Card.SubHeader>

      {isValidSquaresSelections ? (
        <MarketLayout.Markets
          markets={markets}
          marketHeader={marketHeader}
          showVersusDivider={showVersusDivider}
          sport={sport}
          league={league}
          competitionType={competitionType}
          mode={mode}
          type="squares"
          corner={corner}
          versusText={versusText}
          updateMarket={updateMarket}
          onSelectionClick={onSelectionClick}
          isSelectedBuilder={isSelectedBuilder}
          priceDisplayFormatter={priceDisplayFormatter}
        />
      ) : (
        <div className="cui-text-fg-moderate body-md">
          Market temporarily unavailable.
        </div>
      )}
      {isValidElement(footer) ? (
        <MarketLayout.Footer>{footer}</MarketLayout.Footer>
      ) : footer && typeof footer === "object" && Object.keys(footer).length ? (
        <MarketLayout.Footer {...footer} className="cui-mt-1" />
      ) : null}
    </Card>
  );
};
